import React, { useEffect, useState, useContext } from 'react'
import ModalTest from '../../../BaseModal'
import { Button, ButtonContainer, CloseDiv, ContainerOfModal, Header, HeaderTitle, ViewContainer, ViewItem } from '../../../styles/modal-style'
import { useNavigate, useSearchParams } from 'react-router-dom'
import ApiService from '../../../../services/ApiService'
import { X, Trash2, ArrowUpCircle, ArrowDownCircle } from 'lucide-react'
import { Label } from '../../../styles/select-style'
import editPen from '../../../../assets/certificates/Tables/pencil.svg'
import { useTenant } from '../../../../contexts/TenantContext'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import ConfirmationModal from '../../../ModalConfirmation'
import ModalContext from '../../../../contexts/ModalContext'
import StockContext from '../../../../contexts/StockContext'
import { format } from 'date-fns'
import { formatMovementQuantity, getMovementDirection } from '../../../../utils/formatMovementQuantity'
import styled from 'styled-components'
import OrdenoLoading from '../../../OrdenoLoading'

interface Supplier {
  id: number
  companyName: string
  contactName?: string
}

interface Resource {
  id: number
  name: string
  code: string
  codeId?: number | null
  category: { id: number, name: string } | null
  type: { id: number, name: string } | null
  pricePerKg: number
  prices?: Array<{
    id: number
    price: string
    unitOfMeasurement: {
      id: number
      name: string
    }
  }>
  description: string
  suppliers: Supplier[]
  totalWeight?: number
  lengthInMm?: number
  unitQuantity?: number
  stockUnit?: string
  dimension1?: number | null
  dimension2?: number | null
  material?: string | null
  format?: string | null
  fabrication?: string | null
  ncm?: string | null
  shelf?: string | null
  stockLocation?: string | null
  stockStreet?: string | null
  colors?: Array<{ id: number; itemColorId: number; name: string; pantone?: { code: string; name?: string } | null }>
  isActivated?: boolean
}

const ViewResourceModal = () => {
  const [searchParams] = useSearchParams()
  const { stock, setStock } = useContext(StockContext)
  const { setModalActive, setModalSection, setItIsUpdate, modalActive, modalSection } = useContext(ModalContext)
  const idFromUrl = searchParams.get('id')
  const idFromContext = stock?.isResource ? String(stock.id) : null
  const id = idFromUrl || idFromContext
  const isModalMode = modalActive && modalSection === 'viewResource'
  const navigate = useNavigate()
  const [resource, setResource] = useState<Resource | null>(null)
  const { setFeedback } = useContext(FeedbackContext)
  const { isFashionTenant } = useTenant()
  const itemName = isFashionTenant ? 'Aviamento' : 'Insumo'
  const redirectParam = searchParams.get('redirect')
  const resourceRedirectPath = redirectParam || (isFashionTenant ? '/panel/stock/resource' : '/panel/resource')
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, resourceId: null as number | null, resourceName: '' })
  const [movements, setMovements] = useState<any[]>([])
  const [loadingMovements, setLoadingMovements] = useState<boolean>(false)

  const handleClose = () => {
    if (isModalMode) {
      setModalActive(false)
    } else {
      navigate(resourceRedirectPath)
    }
  }

  const handleNewMovimentClick = () => {
    if (!resource) {
      return;
    }
    setStock({
      code: resource.code,
      codeId: resource.codeId,
      description: resource.name,
      totalWeight: resource.totalWeight != null ? String(resource.totalWeight) : '0',
      lengthInMm: resource.lengthInMm != null ? String(resource.lengthInMm) : '0',
      unitQuantity: (resource as any).unitQuantity != null ? String((resource as any).unitQuantity) : '0',
      stockUnit: resource.stockUnit,
      isResource: true,
      id: resource.id,
      resourceId: resource.id,
    } as any)
    setItIsUpdate(true)
    setModalSection('modalEntryOrExit')
    setModalActive(true)
    if (!isModalMode) {
      navigate(resourceRedirectPath)
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      if (id) {
        try {
          const response = await ApiService.resources.findById(Number(id))
          const data = response.data
          setResource(data)
        } catch (error) {
          console.log(error)
        }
      }
    }
    fetchData()
  }, [id, modalActive])

  useEffect(() => {
    const fetchHistory = async () => {
      if (id) {
        setLoadingMovements(true)
        try {
          const res = await ApiService.movements.findByResourceId(Number(id))
          if (res?.data) {
            setMovements(res.data)
          }
        } catch (err) {
          console.error(err)
        } finally {
          setLoadingMovements(false)
        }
      }
    }
    fetchHistory()
  }, [id, modalActive])

  const formatPrice = (price?: number | null) => {
    if (!price) return 'N/A'
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price)
  }

  const formatPrices = (prices?: any[]) => {
    if (!prices || prices.length === 0) return 'N/A'

    return prices.map(price => {
      const formattedPrice = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(parseFloat(price.price))
      return `${formattedPrice}/${price.UnitOfMeasurement?.name || price.unitOfMeasurement?.name || 'N/A'}`
    }).join(', ')
  }

  const getSupplierDisplayName = (supplier: any): string => {
    if (!supplier) return ''
    const baseName = supplier.companyName || supplier.contactName || ''
    if (!supplier.isMain) return baseName
    const tipo = supplier.tipoEstablishment || supplier.tipoEstabelecimento || (supplier.cnpj ? (supplier.cnpj.replace(/\D/g, '').substring(8, 12) === '0001' ? 'Matriz' : 'Filial') : '')
    return `${baseName}${tipo ? ` - ${tipo}` : ''}`
  }

  const handleDeleteClick = () => {
    if (!id || !resource) return
    setDeleteModal({ isOpen: true, resourceId: parseInt(id), resourceName: resource.name })
  }

  const handleConfirmDelete = async () => {
    if (!deleteModal.resourceId || !resource) return
    const newStatus = resource.isActivated === false
    try {
      await ApiService.resources.update(Number(deleteModal.resourceId), {
        isActivated: newStatus
      })
      setFeedback({ 
        message: `${itemName} ${newStatus ? 'ativado' : 'inativado'} com sucesso!`, 
        isOpen: true, 
        statusOk: true 
      })
      if (isModalMode) {
        setModalActive(false)
      } else {
        navigate(resourceRedirectPath)
      }
      window.location.reload()
    } catch (error: any) {
      let errorMessage = `Erro ao ${newStatus ? 'ativar' : 'inativar'} insumo`
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.message) {
        errorMessage = error.message
      }
      setFeedback({
        message: errorMessage,
        isOpen: true,
        statusOk: false
      })
    } finally {
      setDeleteModal({ isOpen: false, resourceId: null, resourceName: '' })
    }
  }

  const handleCancelDelete = () => {
    setDeleteModal({ isOpen: false, resourceId: null, resourceName: '' })
  }

  const modalContent = (
    <ContainerOfModal width='1100px'>
      <Header>
        <HeaderTitle>{itemName}: {resource?.name}</HeaderTitle>
        <CloseDiv onClick={handleClose}> <X size={20} /></CloseDiv>
      </Header>
      <ViewContainer style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
        <ViewItem>
          <span>Categoria</span>
          <Label>{resource?.category?.name || 'N/A'}</Label>
        </ViewItem>
        <ViewItem>
          <span>Tipo</span>
          <Label>{resource?.type?.name || 'N/A'}</Label>
        </ViewItem>
        <ViewItem>
          <span>Código</span>
          <Label>{resource?.code || 'N/A'}</Label>
        </ViewItem>
        <ViewItem>
          <span>Preços</span>
          <Label>{formatPrices(resource?.prices)}</Label>
        </ViewItem>
        <ViewItem>
          <span>Fornecedores</span>
          <Label>{resource?.suppliers.map((supplier: any) => getSupplierDisplayName(supplier)).join(', ') || 'N/A'}</Label>
        </ViewItem>
        <ViewItem>
          <span>Peso Total</span>
          <Label>{resource?.totalWeight != null ? resource.totalWeight : 'N/A'}</Label>
        </ViewItem>
        <ViewItem>
          <span>Comprimento (mm)</span>
          <Label>{resource?.lengthInMm != null ? resource.lengthInMm : 'N/A'}</Label>
        </ViewItem>
        <ViewItem>
          <span>Quantidade</span>
          <Label>{resource?.unitQuantity != null ? resource.unitQuantity : 'N/A'}</Label>
        </ViewItem>
        <ViewItem>
          <span>Unidade de Estoque</span>
          <Label>{resource?.stockUnit || 'N/A'}</Label>
        </ViewItem>
        <ViewItem>
          <span>Dimensão 1</span>
          <Label>{resource?.dimension1 != null ? resource.dimension1 : 'N/A'}</Label>
        </ViewItem>
        <ViewItem>
          <span>Dimensão 2</span>
          <Label>{resource?.dimension2 != null ? resource.dimension2 : 'N/A'}</Label>
        </ViewItem>
        <ViewItem>
          <span>Material</span>
          <Label>{resource?.material || 'N/A'}</Label>
        </ViewItem>
        <ViewItem>
          <span>Formato</span>
          <Label>{resource?.format || 'N/A'}</Label>
        </ViewItem>
        <ViewItem>
          <span>Fabricação</span>
          <Label>{resource?.fabrication || 'N/A'}</Label>
        </ViewItem>
        <ViewItem>
          <span>NCM</span>
          <Label>{resource?.ncm || 'N/A'}</Label>
        </ViewItem>
        <ViewItem>
          <span>Prateleira</span>
          <Label>{resource?.shelf || 'N/A'}</Label>
        </ViewItem>
        <ViewItem>
          <span>Localização</span>
          <Label>{resource?.stockLocation || 'N/A'}</Label>
        </ViewItem>
        <ViewItem>
          <span>Rua do Estoque</span>
          <Label>{resource?.stockStreet || 'N/A'}</Label>
        </ViewItem>
        {isFashionTenant && (
          <ViewItem>
            <span>Cores</span>
            <Label>
              {resource?.colors && resource.colors.length > 0
                ? resource.colors
                    .map(c => `${c.name}${c.pantone?.code ? ` (${c.pantone.code})` : ''}`)
                    .join(', ')
                : 'N/A'}
            </Label>
          </ViewItem>
        )}
        <ViewItem>
          <span>Descrição</span>
          <Label>{resource?.description || 'N/A'}</Label>
        </ViewItem>
      </ViewContainer>

      <HistorySection>
        <HistoryTitle>Histórico de Movimentações</HistoryTitle>
        {loadingMovements ? (
          <OrdenoLoading variant='inline' size='sm' text='Carregando histórico...' style={{ padding: '1.5rem 0' }} />
        ) : movements.length === 0 ? (
          <p style={{ color: '#6b7280', fontSize: '14px' }}>Nenhuma movimentação registrada.</p>
        ) : (
          <HistoryTable>
            <thead>
              <tr>
                <HistoryTh>Data</HistoryTh>
                <HistoryTh>Tipo</HistoryTh>
                <HistoryTh>Movimento</HistoryTh>
                <HistoryTh>Novo Peso</HistoryTh>
                <HistoryTh>Novo Comp.</HistoryTh>
                <HistoryTh>Operação/Pedido</HistoryTh>
                <HistoryTh>Usuário</HistoryTh>
              </tr>
            </thead>
            <tbody>
              {movements.map((mv) => {
                const direction = getMovementDirection(mv)
                const isOrder = mv.ploomesOrderId > 1
                const isEntry = !isOrder && direction === 'IN'
                const type = isOrder ? 'order' : isEntry ? 'entry' : 'exit'
                const label = isOrder ? 'Pedido' : isEntry ? 'Entrada' : 'Saída'
                return (
                  <tr
                    key={mv.id}
                    title={
                      (mv.User?.name || mv.User?.email) && mv.createdAt
                        ? `Atualizado por ${mv.User.name || mv.User.email} em ${
                          format(new Date(mv.createdAt), 'dd/MM/yyyy - HH:mm')}h${
                          mv.observation ? `\nObservação: ${mv.observation}` : ''
                        }`
                        : undefined
                    }
                  >
                    <HistoryTd>{format(new Date(mv.createdAt), 'dd/MM/yyyy HH:mm')}</HistoryTd>
                    <HistoryTd>
                      <HistoryBadge type={type}>{label}</HistoryBadge>
                    </HistoryTd>
                    <HistoryTd>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        {getMovementDirection(mv) === 'IN'
                          ? <ArrowUpCircle color="green" size={18} />
                          : <ArrowDownCircle color="red" size={18} />}
                        {formatMovementQuantity(mv.quantity, mv.unitOfMeasurement)}
                      </span>
                    </HistoryTd>
                    <HistoryTd>{mv.newTotalWeight != null ? `${mv.newTotalWeight.toFixed(2)} kg` : '-'}</HistoryTd>
                    <HistoryTd>{mv.newLengthInMm != null ? `${mv.newLengthInMm.toFixed(0)} mm` : '-'}</HistoryTd>
                    <HistoryTd>{mv.orderName || '-'}</HistoryTd>
                    <HistoryTd>{mv.User?.name || mv.User?.email || '-'}</HistoryTd>
                  </tr>
                )
              })}
            </tbody>
          </HistoryTable>
        )}
      </HistorySection>

      <ButtonContainer>
        <Button
          onClick={handleDeleteClick}
          style={{
            backgroundColor: 'transparent',
            color: resource?.isActivated === false ? '#10B981' : '#F87171',
            border: resource?.isActivated === false ? '1px solid #10B981' : '1px solid #F87171'
          }}
        >
          {resource?.isActivated === false ? 'Ativar' : 'Inativar'}
        </Button>
        {isFashionTenant && (
          <Button
            onClick={() => navigate(`/panel/stock/resource/create?sourceId=${id}&variation=true`)}
            style={{
              backgroundColor: 'transparent',
              color: '#8B5CF6',
              border: '1px solid #8B5CF6'
            }}
          >
            Criar Variação
          </Button>
        )}
        {resource?.codeId && (
          <Button
            onClick={handleNewMovimentClick}
            style={{
              backgroundColor: 'transparent',
              color: '#10B981',
              border: '1px solid #10B981'
            }}
          >
            Novo movimento
          </Button>
        )}
        <Button 
          onClick={() => navigate(`/panel/resource/edit?id=${id}`)}
          style={{
            backgroundColor: 'transparent',
            color: '#6b7280',
            border: '1px solid #d1d5db'
          }}
        >
          <img src={editPen} alt='edit' style={{ filter: 'grayscale(1)' }} /> Editar
        </Button>
      </ButtonContainer>
    </ContainerOfModal>
  );

  return (
    <>
      {isModalMode ? (
        modalContent
      ) : (
        <ModalTest>
          {modalContent}
        </ModalTest>
      )}
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        title={resource?.isActivated === false ? "Confirmar Ativação" : "Confirmar Inativação"}
        message={
          <div>
            <p>Tem certeza que deseja {resource?.isActivated === false ? 'ativar' : 'inativar'} o {itemName.toLowerCase()}: &quot;{deleteModal.resourceName}&quot;?</p>
          </div>
        }
        actions={[
          { label: 'Cancelar', onClick: handleCancelDelete, variant: 'outline' },
          { label: resource?.isActivated === false ? 'Ativar' : 'Inativar', onClick: handleConfirmDelete, variant: resource?.isActivated === false ? 'primary' : 'danger' },
        ]}
      />
    </>
  )
}

export default ViewResourceModal

const HistorySection = styled.div`
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #ebebeb;
`

const HistoryTitle = styled.h3`
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 1rem;
  color: #111827;
`

const HistoryTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  margin-bottom: 1.5rem;
`

const HistoryTh = styled.th`
  text-align: left;
  padding: 10px 12px;
  border-bottom: 2px solid #ebebeb;
  color: #4b5563;
  font-weight: 600;
`

const HistoryTd = styled.td`
  padding: 10px 12px;
  border-bottom: 1px solid #f3f4f6;
  color: #1f2937;
`

const HistoryBadge = styled.span<{ type: string }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 500;
  background-color: ${({ type }) => (type === 'entry' ? '#DEF7EC' : type === 'exit' ? '#FDE8E8' : '#FEF08A')};
  color: ${({ type }) => (type === 'entry' ? '#03543F' : type === 'exit' ? '#9B1C1C' : '#713F12')};
`
